/* eslint-disable react/jsx-key */
import React, { useEffect, useMemo, useRef } from 'react';

import { TriangleDownIcon, TriangleUpIcon } from '@chakra-ui/icons';
import {
  Flex,
  HStack,
  Input,
  InputGroup,
  InputLeftAddon,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Box,
  CircularProgress,
  useDisclosure,
} from '@chakra-ui/react';
import { format } from 'date-fns';
import { isEmpty } from 'ramda';
import { useLocation } from 'react-router-dom';
import { useTable, useSortBy } from 'react-table';

import UserDetails from 'components/modules/UserDetails';
import UsersFilters from 'components/modules/UsersFilters';
import UsersNotifyExemptions from 'components/modules/UsersNotifyExemptions';
import Button from 'components/primitives/Button';
import PaginationView from 'components/primitives/Pagination';
import { Search, Listing as ListingIcon } from 'components/primitives/SVG';
import Typography from 'components/primitives/Typography';
import { useTheme } from 'utils/theme';

import { UsersGeneratedProps } from './Users.props';
import { FilterOption } from './Users.style';

const widthPerAccessor = {
  undefined: undefined,
};

const headerText: Record<string, string> = {
  all: 'ALL USERS',
  last5days: 'JOINED IN THE LAST 5 DAYS"',
  active: 'ALL ACTIVE USERS',
  suspended: 'ALL SUSPENDED USERS',
};

const UsersView = ({
  data,
  meta,
  isLoading,
  tableOptions,
  setTableOptions,
  ...props
}: UsersGeneratedProps): JSX.Element => {
  const theme = useTheme();
  const filterModal = useDisclosure();
  const exemptionsModal = useDisclosure();
  const location = useLocation();
  const searchRef = useRef() as React.MutableRefObject<HTMLInputElement>;
  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search]
  );

  const filter = useMemo(() => query.get('filter'), [location.search]);
  const search = useMemo(() => query.get('search'), [location.search]);
  const id = useMemo(() => query.get('id'), [location.search]);

  const currentIndex = useMemo(
    () => (id ? data.findIndex((d) => d.id === id) : -1),
    [data, id]
  );

  const columns = React.useMemo(
    () => [
      {
        Header: 'Name',
        accessor: 'firstName',
        Cell: ({ row }: any) =>
          `${row.original.firstName} ${row.original.lastName}`,
      },
      {
        Header: 'Email',
        accessor: 'email',
      },
      {
        Header: 'Phone #',
        accessor: 'phone',
      },
      {
        Header: 'Selling',
        accessor: 'sellingCount',
      },
      {
        Header: 'Buying',
        accessor: 'buyingCount',
      },
      {
        Header: 'Status',
        accessor: 'status',
      },
      {
        Header: 'Date Joined',
        accessor: 'dateJoined',
        Cell: ({ row }: any) =>
          format(new Date(row.original.dateJoined), 'MM/dd/y kk:mm:ss a'),
      },
    ],
    []
  );

  const onPrev = () => {
    if (currentIndex > 0) props.setUser(data[currentIndex - 1]);
  };

  const onNext = () => {
    if (currentIndex < data.length - 1) {
      props.setUser(data[currentIndex + 1]);
    }
  };

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    rows,
    prepareRow,
    state: { sortBy },
  } = useTable(
    {
      data,
      // @ts-ignore
      columns,
      manualSortBy: true,
      disableMultiSort: true,
      disableSortBy: isLoading,
      initialState: {
        sortBy: [
          {
            id: 'dateJoined',
            desc: true,
          },
        ],
      },
    },
    useSortBy
  );

  const onPaginate = (page: number, pageSize: number) => {
    setTableOptions((prevState) => {
      return {
        ...prevState,
        page: prevState.limit !== pageSize ? 1 : page,
        limit: pageSize,
      };
    });
  };

  useEffect(() => {
    setTableOptions((prevState) => {
      if (!isEmpty(sortBy)) {
        const { id, desc } = sortBy[0];
        return {
          ...prevState,
          sort: id,
          direction: desc ? 'desc' : 'asc',
        };
      } else if (isEmpty(sortBy) && !!prevState.sort) {
        const psCopy = { ...prevState };
        delete psCopy.sort;
        delete psCopy.direction;

        return psCopy;
      }

      return prevState;
    });
  }, [sortBy]);

  return (
    <Flex flexDirection="column">
      <Flex flex={1}>
        <Typography variant="type5" weight="700" color="primary">
          USERS
        </Typography>
        <HStack mt={1} ml={20} spacing={10} alignItems="flex-start">
          <FilterOption
            borderBottom={`5px solid ${
              !search && !filter ? theme.colors.special4 : 'transparent'
            }`}
            onClick={() => {
              searchRef.current.value = '';
              props.onClearFilter();
            }}
          >
            <Typography
              variant="type8"
              weight="600"
              color={!location.search && !filter ? 'primary' : 'shade1'}
            >
              All
            </Typography>
          </FilterOption>
          <FilterOption
            onClick={() => props.onSetFilter('active', 'filter')}
            borderBottom={`5px solid ${
              filter === 'active' ? theme.colors.special4 : 'transparent'
            }`}
          >
            <Typography
              variant="type8"
              weight="600"
              color={filter === 'active' ? 'primary' : 'shade1'}
            >
              Active
            </Typography>
          </FilterOption>
          <FilterOption
            onClick={() => props.onSetFilter('suspended', 'filter')}
            borderBottom={`5px solid ${
              filter === 'suspended' ? theme.colors.special4 : 'transparent'
            }`}
          >
            <Typography
              variant="type8"
              weight="600"
              color={filter === 'suspended' ? 'primary' : 'shade1'}
            >
              Suspended
            </Typography>
          </FilterOption>
          <FilterOption
            onClick={() => props.onSetFilter('last5days', 'filter')}
            borderBottom={`5px solid ${
              filter === 'last5days' ? theme.colors.special4 : 'transparent'
            }`}
          >
            <Typography
              variant="type8"
              weight="600"
              color={filter === 'last5days' ? 'primary' : 'shade1'}
            >
              Last 5 days
            </Typography>
          </FilterOption>
        </HStack>
      </Flex>
      <Flex
        mt={5}
        flex={1}
        borderRadius={10}
        p={5}
        minHeight="500px"
        bg="#fff"
        filter="drop-shadow(0px 2px 10px rgba(149, 149, 149, 0.18))"
        flexDirection="column"
      >
        <Flex
          flexDir="row"
          width="100%"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Flex height="30px" alignItems="center">
            <ListingIcon color="shade10" width={12} height={12} />
            <Flex mx={3}>
              <Typography variant="type8" weight="600" color="shade10">
                {headerText[filter || 'all']}
              </Typography>
            </Flex>
            {isLoading ? (
              <CircularProgress isIndeterminate size="20px" color="primary" />
            ) : (
              <Typography variant="type10" color="shade5">
                {meta.count} user{meta.count > 1 && 's'}
              </Typography>
            )}
          </Flex>
          <HStack spacing={5}>
            <InputGroup size="sm" width={300}>
              <InputLeftAddon borderRadius={10} backgroundColor="transparent">
                <Search width="14px" height="14px" />
              </InputLeftAddon>
              <Input
                ref={searchRef}
                onChange={(a) => props.onSearchKey(a.target.value)}
                borderLeft="none"
                placeholder="Search"
                borderRadius={10}
              />
            </InputGroup>
            <Button variant="small" onClick={filterModal.onOpen}>
              Filter
            </Button>
            <Button
              variant="small"
              kind="secondary"
              onClick={exemptionsModal.onOpen}
            >
              Exemptions
            </Button>
          </HStack>
        </Flex>
        <Table size="sm" mt={7} {...getTableProps()}>
          <Thead backgroundColor="shade12">
            {headerGroups.map((headerGroup) => (
              <Tr {...headerGroup.getHeaderGroupProps()}>
                {headerGroup.headers.map((column) => (
                  <Th
                    {
                      // @ts-ignore
                      ...column.getHeaderProps(column.getSortByToggleProps())
                    }
                  >
                    <Flex flexDirection="row">
                      <Typography variant="type9" weight="600" color="shade5">
                        {column.Header}
                      </Typography>
                      <Flex ml={2}>
                        {
                          // @ts-ignore
                          column.isSorted ? (
                            <Box>
                              {
                                // @ts-ignore
                                column.isSortedDesc ? (
                                  <TriangleDownIcon aria-label="sorted descending" />
                                ) : (
                                  <TriangleUpIcon aria-label="sorted ascending" />
                                )
                              }
                            </Box>
                          ) : (
                            <Box width="12px" height="5px" />
                          )
                        }
                      </Flex>
                    </Flex>
                  </Th>
                ))}
              </Tr>
            ))}
          </Thead>
          <Tbody {...getTableBodyProps()}>
            {rows.map((row, i) => {
              prepareRow(row);
              return (
                <Tr
                  backgroundColor={i % 2 !== 0 ? 'shade18' : 'transparent'}
                  cursor="pointer"
                  onClick={() => props.setUser(row.original)}
                  {...row.getRowProps()}
                >
                  {row.cells.map((cell) => (
                    <Td
                      {...cell.getCellProps()}
                      // @ts-ignore
                      width={widthPerAccessor[cell.column.id]}
                    >
                      <Typography variant="type8" color="shade5">
                        {cell.render('Cell')}
                      </Typography>
                    </Td>
                  ))}
                </Tr>
              );
            })}
          </Tbody>
        </Table>
        <UserDetails
          id={id}
          onClose={() => props.setUser(undefined)}
          onNext={onNext}
          onPrev={onPrev}
          isOpen={Boolean(id)}
          disablePrev={currentIndex === 0}
          disableNext={currentIndex === data.length - 1}
        />
        <UsersFilters {...filterModal} />
        <UsersNotifyExemptions {...exemptionsModal} />
        <Flex mt={5} flexDir="row" width="100%" justifyContent="flex-end">
          <PaginationView
            disabled={isLoading}
            total={meta.count}
            current={meta.page}
            pageSize={tableOptions.limit}
            onChange={onPaginate}
          />
        </Flex>
      </Flex>
    </Flex>
  );
};

export default UsersView;
